import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import FinanceShell from "@/components/finance/FinanceShell";
import { formatTRY } from "@/components/finance/financeLabels";

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

const TABS = [
    { key: "pnl", label: "Kâr-Zarar (P&L)" },
    { key: "trial", label: "Mizan" },
    { key: "vat", label: "KDV Raporu" },
    { key: "aging", label: "Yaşlandırma" },
];

export default function ReportsPage() {
    const [tab, setTab] = useState("pnl");
    const [from, setFrom] = useState(startOfMonth());
    const [to, setTo] = useState(today());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        setData(null);  // Clear stale data so wrong-shape never reaches sub-components
        try {
            let url = "";
            if (tab === "pnl") url = `/admin/finance/reports/profit-loss?date_from=${from}&date_to=${to}`;
            else if (tab === "trial") url = `/admin/finance/reports/trial-balance?as_of=${to}`;
            else if (tab === "vat") url = `/admin/finance/reports/vat?date_from=${from}&date_to=${to}`;
            else if (tab === "aging") url = `/admin/finance/reports/aging?party_type=customer`;
            const r = await api.get(url);
            setData(r.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, from, to]);

    return (
        <FinanceShell>
            <div className="flex flex-wrap gap-2 mb-4">
                <div className="flex gap-1 flex-1">
                    {TABS.map((t) => (
                        <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${tab === t.key ? "bg-sky-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 hover:bg-slate-200"}`} data-testid={`report-tab-${t.key}`}>
                            {t.label}
                        </button>
                    ))}
                </div>
                {tab !== "aging" && (
                    <>
                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm" data-testid="report-from" />
                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm" data-testid="report-to" />
                    </>
                )}
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5" data-testid="report-content">
                {loading || !data ? <div className="py-10 text-center text-slate-500">Yükleniyor...</div> : (
                    tab === "pnl" ? <PnLReport data={data} />
                    : tab === "trial" ? <TrialBalanceReport data={data} />
                    : tab === "vat" ? <VatReport data={data} />
                    : tab === "aging" ? <AgingReport data={data} /> : null
                )}
            </div>
        </FinanceShell>
    );
}

function PnLRow({ label, value, bold, neg }) {
    return (
        <div className={`flex items-center justify-between py-2 ${bold ? "border-t border-slate-200 dark:border-slate-700 mt-2 pt-3" : ""}`}>
            <span className={`${bold ? "font-bold text-slate-900 dark:text-white" : "text-slate-600 dark:text-slate-400"}`}>{label}</span>
            <span className={`tabular-nums ${bold ? "text-xl font-extrabold" : "text-base font-semibold"} ${neg ? "text-rose-600" : value < 0 ? "text-rose-600" : "text-emerald-600"}`}>{formatTRY(value)}</span>
        </div>
    );
}

function PnLReport({ data }) {
    return (
        <div className="max-w-2xl">
            <h3 className="font-bold text-lg mb-3">Gelir-Gider Tablosu</h3>
            <PnLRow label="Tahsilat (Gelir)" value={data.income} />
            <PnLRow label="(−) İadeler" value={data.refunds} neg />
            <PnLRow label="Net Hasılat" value={data.net_revenue} bold />
            <PnLRow label="(−) Tedarikçi Maliyeti" value={data.cost_of_sales} neg />
            <PnLRow label="Brüt Kâr" value={data.gross_profit} bold />
            <PnLRow label="(−) Acente Komisyonları" value={data.commissions} neg />
            <PnLRow label="(−) Faaliyet Giderleri" value={data.operating_expenses} neg />
            <PnLRow label="Faaliyet Kârı" value={data.operating_profit} bold />
            <PnLRow label="(−) Vergi" value={data.tax} neg />
            <PnLRow label="NET KÂR" value={data.net_profit} bold />
        </div>
    );
}

function TrialBalanceReport({ data }) {
    const rows = Array.isArray(data) ? data : [];
    const totals = rows.reduce((a, r) => ({ debit: a.debit + (r.debit || 0), credit: a.credit + (r.credit || 0), balance: a.balance + (r.balance || 0) }), { debit: 0, credit: 0, balance: 0 });
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    <tr><th className="text-left px-2 py-2">Kod</th><th className="text-left px-2 py-2">Ad</th><th className="text-left px-2 py-2">Tür</th><th className="text-right px-2 py-2">Borç</th><th className="text-right px-2 py-2">Alacak</th><th className="text-right px-2 py-2">Bakiye</th></tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.code + i} className="border-t border-slate-100 dark:border-slate-800/50">
                            <td className="px-2 py-1.5 font-mono text-xs">{r.code}</td>
                            <td className="px-2 py-1.5 font-semibold">{r.name}</td>
                            <td className="px-2 py-1.5 text-xs">{r.party_type || r.account_type || r.type}</td>
                            <td className="px-2 py-1.5 text-right">{formatTRY(r.debit)}</td>
                            <td className="px-2 py-1.5 text-right">{formatTRY(r.credit)}</td>
                            <td className={`px-2 py-1.5 text-right font-bold ${r.balance > 0.01 ? "text-emerald-600" : r.balance < -0.01 ? "text-rose-600" : ""}`}>{formatTRY(r.balance)}</td>
                        </tr>
                    ))}
                    <tr className="border-t-2 border-slate-300 dark:border-slate-700 font-bold">
                        <td colSpan={3} className="px-2 py-2 text-right">TOPLAM</td>
                        <td className="px-2 py-2 text-right">{formatTRY(totals.debit)}</td>
                        <td className="px-2 py-2 text-right">{formatTRY(totals.credit)}</td>
                        <td className="px-2 py-2 text-right">{formatTRY(totals.balance)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function VatReport({ data }) {
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    return (
        <div className="max-w-2xl">
            <h3 className="font-bold text-lg mb-3">KDV Detayı</h3>
            <table className="w-full text-sm mb-4">
                <thead className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    <tr><th className="text-left px-2 py-2">Oran</th><th className="text-right px-2 py-2">Matrah</th><th className="text-right px-2 py-2">KDV</th><th className="text-right px-2 py-2">İşlem</th></tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.rate} className="border-t border-slate-100 dark:border-slate-800/50">
                            <td className="px-2 py-2 font-bold">%{r.rate}</td>
                            <td className="px-2 py-2 text-right">{formatTRY(r.base)}</td>
                            <td className="px-2 py-2 text-right text-orange-600 font-semibold">{formatTRY(r.vat)}</td>
                            <td className="px-2 py-2 text-right text-xs text-slate-500">{r.count}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-slate-500">Bu dönem için KDV kaydı yok</td></tr>}
                </tbody>
            </table>
            <div className="text-right text-lg font-bold">Toplam KDV: <span className="text-orange-600">{formatTRY(data?.total_vat || 0)}</span></div>
        </div>
    );
}

function AgingReport({ data }) {
    const rows = Array.isArray(data) ? data : [];
    return (
        <div className="overflow-x-auto">
            <h3 className="font-bold text-lg mb-3">Müşteri Yaşlandırma (Outstanding)</h3>
            <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    <tr>
                        <th className="text-left px-2 py-2">Cari</th>
                        <th className="text-right px-2 py-2">0-30 gün</th>
                        <th className="text-right px-2 py-2">30-60 gün</th>
                        <th className="text-right px-2 py-2">60-90 gün</th>
                        <th className="text-right px-2 py-2">90+ gün</th>
                        <th className="text-right px-2 py-2">Toplam Bakiye</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.code} className="border-t border-slate-100 dark:border-slate-800/50">
                            <td className="px-2 py-1.5"><b>{r.name}</b> <span className="text-xs text-slate-400">({r.code})</span></td>
                            <td className="px-2 py-1.5 text-right">{formatTRY(r["0_30"])}</td>
                            <td className="px-2 py-1.5 text-right">{formatTRY(r["30_60"])}</td>
                            <td className="px-2 py-1.5 text-right text-amber-600">{formatTRY(r["60_90"])}</td>
                            <td className="px-2 py-1.5 text-right text-rose-600">{formatTRY(r["90_plus"])}</td>
                            <td className="px-2 py-1.5 text-right font-bold">{formatTRY(r.balance)}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-500">Açık bakiye yok</td></tr>}
                </tbody>
            </table>
        </div>
    );
}
